import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, LayoutDashboard, Menu, Heart, Search, X, MessageCircle, Instagram, Handshake, Package, CalendarClock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '@/components/ui/sheet';

const Navbar = () => {
  const { user, logout } = useAuth();
  const { cartCount } = useCart();
  const { wishlistIds } = useWishlist();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.filter(c => c.visible));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const term = searchTerm.trim();
    if (!term) return;
    navigate(`/products?q=${encodeURIComponent(term)}`);
    setSearchOpen(false);
    setSearchTerm('');
  };

  return (
    <nav className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-accent/20" data-testid="main-navbar">
      {/* Search Overlay */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-start justify-center pt-24 animate-in fade-in duration-200"
          onClick={() => setSearchOpen(false)}
          data-testid="search-overlay"
        >
          <form
            onSubmit={handleSearchSubmit}
            onClick={(e) => e.stopPropagation()}
            className="bg-background border border-border rounded-2xl shadow-2xl p-6 w-[92%] max-w-2xl"
          >
            <div className="flex items-center gap-3">
              <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              <Input
                autoFocus
                type="search"
                placeholder="Search sarees, dupattas, fabrics..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="rounded-full border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-lg"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                className="p-1 hover:text-primary transition-colors"
                data-testid="search-close-button"
                aria-label="Close search"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-3 pl-8">
              Press <kbd className="px-1.5 py-0.5 border border-border rounded text-[10px]">Enter</kbd> to search
            </p>
          </form>
        </div>
      )}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Left: Hamburger Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full" data-testid="hamburger-menu">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80">
              <div className="flex flex-col gap-6 mt-8">
                <Link
                  to="/"
                  className="text-lg uppercase tracking-widest hover:text-primary transition-colors"
                  data-testid="mobile-nav-home"
                >
                  Home
                </Link>
                
                <div className="border-t border-border pt-4">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Categories</p>
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      to={`/products?category=${category.name}`}
                      className="block text-lg py-2 hover:text-primary transition-colors"
                      data-testid={`mobile-nav-${category.name.toLowerCase()}`}
                    >
                      {category.name}
                    </Link>
                  ))}
                </div>
                
                <Link
                  to="/products"
                  className="text-lg uppercase tracking-widest hover:text-primary transition-colors"
                  data-testid="mobile-nav-all-products"
                >
                  All Products
                </Link>

                <Link
                  to="/book-call"
                  className="text-lg uppercase tracking-widest hover:text-primary transition-colors flex items-center gap-2"
                  data-testid="mobile-nav-book-call"
                >
                  <CalendarClock className="h-5 w-5" />
                  Book a Call
                </Link>

                <Link
                  to="/wholesale"
                  className="text-lg uppercase tracking-widest hover:text-primary transition-colors flex items-center gap-2"
                  data-testid="mobile-nav-wholesale"
                >
                  <Handshake className="h-5 w-5" />
                  Wholesale
                </Link>

                <div className="border-t border-border pt-4 mt-2">
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Connect</p>
                  <a
                    href="https://wa.me/917621003708"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 py-2 text-sm hover:text-primary transition-colors"
                    data-testid="mobile-nav-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Chat on WhatsApp
                  </a>
                  <a
                    href="https://www.instagram.com/bandhani_by_abdulahadkhatri"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 py-2 text-sm hover:text-primary transition-colors"
                    data-testid="mobile-nav-instagram"
                  >
                    <Instagram className="h-4 w-4" />
                    Follow on Instagram
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>

          {/* Center: Logo + Brand Name */}
          <Link to="/" className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-2 md:gap-4" data-testid="nav-logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_traditional-textiles-2/artifacts/oln1htqm_New%20Logo-Blue.png" 
              alt="Bandhani Heritage Logo" 
              className="h-10 md:h-14 w-auto rounded-none flex-shrink-0"
              style={{ borderRadius: 0 }}
            />
            <div className="hidden sm:flex flex-col items-center min-w-0">
              <span className="text-sm md:text-lg font-light tracking-wider whitespace-nowrap" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                BANDHANI HERITAGE
              </span>
              <span className="text-[8px] md:text-[10px] tracking-wide md:tracking-widest text-muted-foreground whitespace-nowrap">
                BY ABDULAHAD IMTIYAZ KHATRI
              </span>
            </div>
          </Link>

          {/* Right: Cart and User */}
          <div className="flex items-center gap-2 md:gap-4">
            <button
              onClick={() => setSearchOpen(true)}
              className="p-2 hover:text-primary transition-colors"
              data-testid="nav-search-button"
              aria-label="Search products"
            >
              <Search className="h-5 w-5" />
            </button>
            {user && !user.is_admin && user.role !== 'inventory_member' && (
              <Link to="/wishlist" className="relative" data-testid="nav-wishlist-link">
                <Heart className={`h-5 w-5 ${wishlistIds.length > 0 ? 'fill-red-500 text-red-500' : ''}`} />
                {wishlistIds.length > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="wishlist-count-badge">
                    {wishlistIds.length}
                  </span>
                )}
              </Link>
            )}
            {user && !user.is_admin && user.role !== 'inventory_member' && (
              <Link to="/cart" className="relative" data-testid="nav-cart-link">
                <ShoppingCart className="h-5 w-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center" data-testid="cart-count-badge">
                    {cartCount}
                  </span>
                )}
              </Link>
            )}

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full" data-testid="user-menu-trigger">
                    <User className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl">
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile-link">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  {user.is_admin && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="nav-admin-link">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  {(user.is_admin || user.role === 'inventory_member') && (
                    <DropdownMenuItem onClick={() => navigate('/admin/inventory')} data-testid="nav-inventory-link">
                      <Package className="mr-2 h-4 w-4" />
                      Inventory
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout-button">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                className="rounded-full px-6 py-2 uppercase tracking-widest text-xs"
                data-testid="nav-login-button"
              >
                Login
              </Button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;